import { Injectable, effect, signal, computed } from '@angular/core';
import { Subject } from 'rxjs';
import { KARTEX_PRIMARY_NAME } from '../kartex-preset';

export interface layoutConfig {
    preset?: string;
    primary?: string;
    surface?: string | undefined | null;
    darkTheme?: boolean;
    menuMode?: string;
}

interface LayoutState {
    staticMenuDesktopInactive?: boolean;
    overlayMenuActive?: boolean;
    configSidebarVisible?: boolean;
    staticMenuMobileActive?: boolean;
    menuHoverActive?: boolean;
}

interface MenuChangeEvent {
    key: string;
    routeEvent?: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class LayoutService {
    private readonly THEME_STORAGE_KEY = 'kartex-theme-preference';
    /** Kayıtlı tercih sürümü. 2: KarteX A paleti; sürümsüz kayıttaki 'indigo' eski varsayılandır, 'kartex'e taşınır. */
    private readonly THEME_VERSION = 2;

    _config: layoutConfig = {
        preset: 'Aura',
        primary: KARTEX_PRIMARY_NAME,
        surface: null,
        darkTheme: false,
        menuMode: 'static'
    };

    _state: LayoutState = {
        staticMenuDesktopInactive: false,
        overlayMenuActive: false,
        configSidebarVisible: false,
        staticMenuMobileActive: false,
        menuHoverActive: false
    };

    layoutConfig = signal<layoutConfig>(this._config);

    layoutState = signal<LayoutState>(this._state);

    private configUpdate = new Subject<layoutConfig>();

    private overlayOpen = new Subject<any>();

    private menuSource = new Subject<MenuChangeEvent>();

    private resetSource = new Subject();

    menuSource$ = this.menuSource.asObservable();

    resetSource$ = this.resetSource.asObservable();

    configUpdate$ = this.configUpdate.asObservable();

    overlayOpen$ = this.overlayOpen.asObservable();

    theme = computed(() => (this.layoutConfig()?.darkTheme ? 'light' : 'dark'));

    isSidebarActive = computed(() => this.layoutState().overlayMenuActive || this.layoutState().staticMenuMobileActive);

    isDarkTheme = computed(() => this.layoutConfig().darkTheme);

    getPrimary = computed(() => this.layoutConfig().primary);

    getSurface = computed(() => this.layoutConfig().surface);

    isOverlay = computed(() => this.layoutConfig().menuMode === 'overlay');

    transitionComplete = signal<boolean>(false);

    private initialized = false;

    constructor() {
        // Load theme preference from localStorage on initialization
        this.loadThemeFromStorage();

        // Listen for system theme changes
        this.setupSystemThemeListener();

        effect(() => {
            const config = this.layoutConfig();
            if (config) {
                this.onConfigUpdate();
            }
        });

        effect(() => {
            const config = this.layoutConfig();

            if (!this.initialized || !config) {
                this.initialized = true;
                return;
            }

            this.handleDarkModeTransition(config);
        });
    }

    private loadThemeFromStorage(): void {
        try {
            const savedTheme = localStorage.getItem(this.THEME_STORAGE_KEY);
            if (savedTheme !== null) {
                const themeConfig = JSON.parse(savedTheme);
                // Tek seferlik geçiş: A paletinden önceki kayıtta 'indigo' eski varsayılandı (elle seçimle ayırt edilemez).
                if ((themeConfig.themeVersion ?? 1) < this.THEME_VERSION && themeConfig.primary === 'indigo') {
                    themeConfig.primary = KARTEX_PRIMARY_NAME;
                }
                delete themeConfig.themeVersion;
                this._config = { ...this._config, ...themeConfig };
                this.layoutConfig.set(this._config);
            } else {
                // Use system default when no theme is saved
                const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
                this._config.darkTheme = prefersDark;
                this.layoutConfig.set(this._config);
            }
        } catch (error) {
            // Fallback to system default on error
            const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
            this._config.darkTheme = prefersDark;
            this.layoutConfig.set(this._config);
        }
    }

    private setupSystemThemeListener(): void {
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

        const handleSystemThemeChange = (e: MediaQueryListEvent) => {
            // Only update if no theme is saved in localStorage
            const savedTheme = localStorage.getItem(this.THEME_STORAGE_KEY);
            if (savedTheme === null) {
                this._config.darkTheme = e.matches;
                this.layoutConfig.set(this._config);
            }
        };

        // Add listener for system theme changes
        if (mediaQuery.addEventListener) {
            mediaQuery.addEventListener('change', handleSystemThemeChange);
        } else {
            // Fallback for older browsers
            mediaQuery.addListener(handleSystemThemeChange);
        }
    }

    private saveThemeToStorage(): void {
        try {
            const currentConfig = this.layoutConfig();
            const themeConfig = {
                darkTheme: currentConfig.darkTheme,
                preset: currentConfig.preset,
                primary: currentConfig.primary,
                surface: currentConfig.surface,
                menuMode: currentConfig.menuMode,
                themeVersion: this.THEME_VERSION
            };
            localStorage.setItem(this.THEME_STORAGE_KEY, JSON.stringify(themeConfig));
        } catch (error) {
            // Silent error handling
        }
    }

    private handleDarkModeTransition(config: layoutConfig): void {
        if ((document as any).startViewTransition) {
            this.startViewTransition(config);
        } else {
            this.toggleDarkMode(config);
            this.onTransitionEnd();
        }
    }

    private startViewTransition(config: layoutConfig): void {
        const transition = (document as any).startViewTransition(() => {
            this.toggleDarkMode(config);
        });

        transition.ready
            .then(() => {
                this.onTransitionEnd();
            })
            .catch(() => {});
    }

    toggleDarkMode(config?: layoutConfig): void {
        if (config) {
            // If config is provided, use it directly
            if (config.darkTheme) {
                document.documentElement.classList.add('app-dark');
            } else {
                document.documentElement.classList.remove('app-dark');
            }
        } else {
            // Toggle the current dark theme state
            const currentConfig = this.layoutConfig();
            const newConfig = {
                ...currentConfig,
                darkTheme: !currentConfig.darkTheme
            };

            this.layoutConfig.set(newConfig);

            // Update CSS class
            if (newConfig.darkTheme) {
                document.documentElement.classList.add('app-dark');
            } else {
                document.documentElement.classList.remove('app-dark');
            }
        }

        // Save theme preference to localStorage
        this.saveThemeToStorage();
    }

    private onTransitionEnd() {
        this.transitionComplete.set(true);
        setTimeout(() => {
            this.transitionComplete.set(false);
        });
    }

    onMenuToggle() {
        if (this.isOverlay()) {
            this.layoutState.update((prev) => ({ ...prev, overlayMenuActive: !this.layoutState().overlayMenuActive }));

            if (this.layoutState().overlayMenuActive) {
                this.overlayOpen.next(null);
            }
        }

        if (this.isDesktop()) {
            this.layoutState.update((prev) => ({ ...prev, staticMenuDesktopInactive: !this.layoutState().staticMenuDesktopInactive }));
        } else {
            this.layoutState.update((prev) => ({ ...prev, staticMenuMobileActive: !this.layoutState().staticMenuMobileActive }));

            if (this.layoutState().staticMenuMobileActive) {
                this.overlayOpen.next(null);
            }
        }
    }

    isDesktop() {
        return window.innerWidth > 991;
    }

    isMobile() {
        return !this.isDesktop();
    }

    onConfigUpdate() {
        this._config = { ...this.layoutConfig() };
        this.configUpdate.next(this.layoutConfig());

        // Save theme preference to localStorage when config changes
        this.saveThemeToStorage();
    }

    onMenuStateChange(event: MenuChangeEvent) {
        this.menuSource.next(event);
    }

    reset() {
        this.resetSource.next(true);
    }

    // Method to clear saved theme (for testing purposes)
    clearSavedTheme(): void {
        try {
            localStorage.removeItem(this.THEME_STORAGE_KEY);
        } catch (error) {
            // Silent error handling
        }
    }
}
